/**
 * «Mi cuenta»: datos de la persona, sus correos (`#correos`, destino del enlace «Elegir qué correos
 * recibo» de cada correo) y sus tokens de API.
 *
 * Un token de API sirve para que un script o herramienta use el tablero con los mismos permisos
 * de la persona. El valor completo se muestra UNA sola vez, al crearlo: no se puede volver a ver.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { META_CARGA_DE_PAGINA } from '../componentes/BarraCargaPagina';
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router';
import {
  ETIQUETA_ROL,
  ETIQUETA_VENCIMIENTO_TOKEN,
  VENCIMIENTOS_TOKEN,
  VENCIMIENTO_TOKEN_POR_DEFECTO,
  type TokenApi,
  type VencimientoToken,
} from '@cdtec-backlog/contrato';
import { useAvisos } from '../componentes/Avisos';
import { mensajeDeError } from '../lib/api';
import { fechaHora } from '../lib/formato';
import { crearToken, listarTokens, revocarToken } from '../features/cuenta/api';
import { TokenNuevo } from '../features/cuenta/TokenNuevo';
import { useYo } from '../features/auth/sesion';
import { FormularioAvisos } from '../features/correos/FormularioAvisos';

/** Estado de un token en la lista. Revocado gana a vencido: es la causa que decidió la persona. */
function EstadoToken({ token }: { token: TokenApi }) {
  if (token.revocadoEn) return <>Revocado</>;
  if (token.vencido) return <span className="etiqueta etiqueta--vencido">Vencido</span>;
  return <>Activo</>;
}

export function PaginaCuenta() {
  const yo = useYo();
  const cliente = useQueryClient();
  const { mostrar } = useAvisos();
  const [nombre, setNombre] = useState('');
  const [vencimiento, setVencimiento] = useState<VencimientoToken>(VENCIMIENTO_TOKEN_POR_DEFECTO);
  const [recienCreado, setRecienCreado] = useState<string | null>(null);
  const { hash } = useLocation();

  // El enlace de los correos llega con `#correos`: el router no baja solo a la sección.
  useEffect(() => {
    if (hash !== '#correos') return;
    document.getElementById('correos')?.scrollIntoView?.({ block: 'start' });
  }, [hash]);

  const tokens = useQuery({
    queryKey: ['tokens'],
    queryFn: listarTokens,
    meta: META_CARGA_DE_PAGINA,
  });
  const crear = useMutation({
    mutationFn: crearToken,
    onSuccess: (creado) => {
      setRecienCreado(creado.token);
      setNombre('');
      setVencimiento(VENCIMIENTO_TOKEN_POR_DEFECTO);
      void cliente.invalidateQueries({ queryKey: ['tokens'] });
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });
  const revocar = useMutation({
    mutationFn: revocarToken,
    onSuccess: () => {
      mostrar('Token revocado.');
      void cliente.invalidateQueries({ queryKey: ['tokens'] });
    },
    onError: (error) => mostrar(mensajeDeError(error), 'error'),
  });

  const alEnviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (nombre.trim()) crear.mutate({ nombre: nombre.trim(), vencimiento });
  };

  return (
    <main className="pagina">
      <h1>Mi cuenta</h1>
      <p>
        {yo.nombre} · {yo.email} · {ETIQUETA_ROL[yo.rol]}
      </p>

      <section id="correos" className="cuenta__correos" aria-labelledby="titulo-correos">
        <h2 id="titulo-correos">Correos</h2>
        <FormularioAvisos destino="yo" yoId={yo.id} />
      </section>

      <section aria-labelledby="titulo-tokens">
        <h2 id="titulo-tokens">Tokens de API</h2>
        <p>
          Sirven para que un script o herramienta usen el tablero con tus mismos permisos. Trátalos
          como una contraseña: no los compartas ni los subas a un repositorio.
        </p>

        {recienCreado && <TokenNuevo token={recienCreado} alCerrar={() => setRecienCreado(null)} />}

        <form className="cuenta__nuevo" onSubmit={alEnviar}>
          <div className="campo">
            <label htmlFor="token-nombre">Nombre del token</label>
            <input
              id="token-nombre"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Claude, script de respaldo…"
              maxLength={80}
            />
          </div>
          <div className="campo campo--vencimiento">
            <label htmlFor="token-vencimiento">Vence en</label>
            <select
              id="token-vencimiento"
              value={vencimiento}
              onChange={(e) => {
                const elegido = VENCIMIENTOS_TOKEN.find((opcion) => opcion === e.target.value);
                if (elegido) setVencimiento(elegido);
              }}
            >
              {VENCIMIENTOS_TOKEN.map((opcion) => (
                <option key={opcion} value={opcion}>
                  {ETIQUETA_VENCIMIENTO_TOKEN[opcion]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="boton boton--primario"
            disabled={crear.isPending || !nombre.trim()}
          >
            Crear token
          </button>
        </form>

        {tokens.isError && <p role="alert">{mensajeDeError(tokens.error)}</p>}
        {tokens.data && tokens.data.length === 0 && (
          <p className="vacio">Todavía no tienes tokens.</p>
        )}
        {tokens.data && tokens.data.length > 0 && (
          <div className="tabla-contenedor">
            <table className="tabla tabla--controles">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Comienza con</th>
                  <th>Creado</th>
                  <th>Último uso</th>
                  <th>Vence</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.data.map((token) => (
                  <tr key={token.id}>
                    <td>{token.nombre}</td>
                    <td>
                      <code>{token.prefijo}…</code>
                    </td>
                    <td>{fechaHora(token.creadoEn)}</td>
                    <td>{token.ultimoUso ? fechaHora(token.ultimoUso) : 'Nunca'}</td>
                    <td>{token.expiraEn ? fechaHora(token.expiraEn) : 'Sin vencimiento'}</td>
                    <td>
                      <EstadoToken token={token} />
                    </td>
                    <td>
                      {!token.revocadoEn && (
                        <button
                          type="button"
                          className="boton boton--peligro"
                          onClick={() => revocar.mutate(token.id)}
                          disabled={revocar.isPending}
                        >
                          Revocar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
